import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Coins, Plus, Pencil, Trash, Calculator, Receipt, ClockCounterClockwise, Tag,
  ChartLineUp, FileText, PaperPlaneTilt, Check, X as XIcon,
} from '@phosphor-icons/react';
import {
  listPricingProfiles, createPricingProfile, updatePricingProfile, deletePricingProfile,
  listPriceRules, createPriceRule, updatePriceRule, deletePriceRule,
  listRateCards, createRateCard, updateRateCard, deleteRateCard,
  listCustomerSkus, upsertCustomerSku, deleteCustomerSku,
  resolvePrice, listPriceAudit,
  getProfitability,
  listQuotes, createQuote, updateQuote, submitQuote, approveQuote,
  rejectQuote, sendQuote, replayQuoteWebhook, deleteQuote,
  getPricingSettings, updatePricingSettings, listQuoteWebhookLog,
} from '../lib/api';

const MARGIN_TYPES = ['percentage', 'fixed', 'tiered'];
const TIERS = ['standard', 'bronze', 'silver', 'gold', 'platinum'];
const GST_RULES = ['inclusive', 'exclusive', 'none'];
const BILLING_CYCLES = ['weekly', 'fortnightly', 'monthly', 'ad_hoc'];
const RULE_KINDS = ['percentage', 'fixed_markup', 'fixed_price', 'tiered'];
const RATE_CATEGORIES = ['storage', 'receiving', 'pickpack', 'freight', 'pallet', 'valueadd'];

const QUOTE_STATUS_COLORS = {
  draft: 'bg-zinc-200 text-zinc-700',
  pending_approval: 'bg-amber-200 text-amber-900',
  approved: 'bg-emerald-200 text-emerald-900',
  rejected: 'bg-rose-200 text-rose-900',
  sent_to_customer: 'bg-sky-200 text-sky-900',
  accepted: 'bg-emerald-600 text-white',
  declined: 'bg-rose-300 text-rose-900',
  expired: 'bg-zinc-300 text-zinc-700',
};

function ruleScheduleStatus(rule) {
  // Returns one of: null (always-on), 'scheduled' (future), 'active' (within window), 'expired' (past).
  const now = new Date();
  const sa = rule.starts_at ? new Date(rule.starts_at) : null;
  const ea = rule.ends_at ? new Date(rule.ends_at) : null;
  if (!sa && !ea) return null;
  if (sa && now < sa) return 'scheduled';
  if (ea && now > ea) return 'expired';
  return 'active';
}

const errMsg = (e, fallback = 'Operation failed') => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(', ');
  if (typeof d === 'string') return d;
  return fallback;
};

// =============================================================================
// Profile Editor
// =============================================================================
function ProfileEditor({ open, onClose, initial, onSaved }) {
  const blank = {
    name: '', customer_email: '', tier: 'standard', margin_type: 'percentage',
    default_margin: 0, tiered_brackets: [], currency: 'AUD', gst_rule: 'inclusive', billing_cycle: 'monthly',
    min_invoice: 0, fuel_levy_pct: 0, payment_terms_days: 14, notes: '',
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) { setForm(initial ? { ...blank, ...initial } : blank); setReason(''); } }, [open, initial]);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name?.trim() || !form.customer_email?.trim()) {
      toast.error('Name and customer email required');
      return;
    }
    setBusy(true);
    try {
      const body = {
        ...form,
        default_margin: Number(form.default_margin) || 0,
        min_invoice: Number(form.min_invoice) || 0,
        fuel_levy_pct: Number(form.fuel_levy_pct) || 0,
        payment_terms_days: parseInt(form.payment_terms_days) || 14,
        tiered_brackets: form.margin_type === 'tiered' ? (form.tiered_brackets || []) : null,
      };
      if (initial?.id) await updatePricingProfile(initial.id, body, reason || undefined);
      else await createPricingProfile(body);
      toast.success(initial ? 'Profile updated' : 'Profile created');
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(errMsg(e, 'Save failed')); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pricing-profile-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit pricing profile' : 'New pricing profile'}</DialogTitle>
          <DialogDescription>Customer-level commercial parameters used as fallback margins.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Name</Label><Input data-testid="profile-name" value={form.name} onChange={(e) => upd('name', e.target.value)} /></div>
          <div><Label>Customer email</Label><Input data-testid="profile-email" value={form.customer_email} onChange={(e) => upd('customer_email', e.target.value)} disabled={!!initial} /></div>
          <div><Label>Tier</Label>
            <Select value={form.tier} onValueChange={(v) => upd('tier', v)}>
              <SelectTrigger data-testid="profile-tier"><SelectValue /></SelectTrigger>
              <SelectContent>{TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Currency</Label><Input data-testid="profile-currency" value={form.currency} onChange={(e) => upd('currency', e.target.value.toUpperCase())} /></div>
          <div><Label>Margin type</Label>
            <Select value={form.margin_type} onValueChange={(v) => upd('margin_type', v)}>
              <SelectTrigger data-testid="profile-margin-type"><SelectValue /></SelectTrigger>
              <SelectContent>{MARGIN_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Default margin{form.margin_type === 'percentage' ? ' (%)' : form.margin_type === 'fixed' ? ' ($)' : ''}</Label>
            <Input data-testid="profile-default-margin" type="number" value={form.default_margin} onChange={(e) => upd('default_margin', e.target.value)} disabled={form.margin_type === 'tiered'} />
          </div>
          {form.margin_type === 'tiered' && (
            <div className="col-span-2">
              <Label>Tiered brackets (cost-based margin %)</Label>
              <div className="space-y-2 mt-1" data-testid="profile-brackets">
                {(form.tiered_brackets || []).map((b, i) => (
                  <div key={i} className="flex gap-2 items-end" data-testid={`profile-bracket-${i}`}>
                    <Input className="w-24" type="number" placeholder="min" value={b.min ?? 0}
                      onChange={(e) => { const list = [...(form.tiered_brackets || [])]; list[i] = { ...b, min: Number(e.target.value) }; upd('tiered_brackets', list); }} />
                    <Input className="w-24" type="number" placeholder="max (∞=blank)" value={b.max ?? ''}
                      onChange={(e) => { const list = [...(form.tiered_brackets || [])]; list[i] = { ...b, max: e.target.value === '' ? null : Number(e.target.value) }; upd('tiered_brackets', list); }} />
                    <Input className="w-24" type="number" placeholder="margin %" value={b.margin_pct ?? 0}
                      onChange={(e) => { const list = [...(form.tiered_brackets || [])]; list[i] = { ...b, margin_pct: Number(e.target.value) }; upd('tiered_brackets', list); }} />
                    <Button size="sm" variant="outline" onClick={() => upd('tiered_brackets', (form.tiered_brackets || []).filter((_, j) => j !== i))}>Remove</Button>
                  </div>
                ))}
                <Button size="sm" variant="outline" data-testid="profile-add-bracket" onClick={() => upd('tiered_brackets', [...(form.tiered_brackets || []), { min: 0, max: null, margin_pct: 0 }])}>+ Add bracket</Button>
              </div>
            </div>
          )}
          <div><Label>GST rule</Label>
            <Select value={form.gst_rule} onValueChange={(v) => upd('gst_rule', v)}>
              <SelectTrigger data-testid="profile-gst"><SelectValue /></SelectTrigger>
              <SelectContent>{GST_RULES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Billing cycle</Label>
            <Select value={form.billing_cycle} onValueChange={(v) => upd('billing_cycle', v)}>
              <SelectTrigger data-testid="profile-billing"><SelectValue /></SelectTrigger>
              <SelectContent>{BILLING_CYCLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Min invoice ($)</Label><Input type="number" value={form.min_invoice} onChange={(e) => upd('min_invoice', e.target.value)} /></div>
          <div><Label>Fuel levy (%)</Label><Input type="number" value={form.fuel_levy_pct} onChange={(e) => upd('fuel_levy_pct', e.target.value)} /></div>
          <div><Label>Payment terms (days)</Label><Input type="number" value={form.payment_terms_days} onChange={(e) => upd('payment_terms_days', e.target.value)} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes || ''} onChange={(e) => upd('notes', e.target.value)} rows={2} /></div>
          {initial && <div className="col-span-2"><Label>Audit reason (optional)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. quarterly review" /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="profile-save" onClick={submit} disabled={busy}>{busy ? 'Saving…' : initial ? 'Save' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Rule Editor
// =============================================================================
function RuleEditor({ open, onClose, initial, onSaved }) {
  const blank = {
    name: '',
    scope: { customer_email: '', sku: '', category: '', service_type: '', warehouse_id: '' },
    action: { kind: 'percentage', margin_pct: 0, amount: 0, price: 0, brackets: [], currency: 'AUD' },
    starts_at: null, ends_at: null, manual_priority: 0, active: true, notes: '',
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      const init = initial ? {
        ...blank, ...initial,
        scope: { ...blank.scope, ...(initial.scope || {}) },
        action: { ...blank.action, ...(initial.action || {}) },
      } : blank;
      setForm(init);
    }
  }, [open, initial]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updScope = (k, v) => setForm((f) => ({ ...f, scope: { ...f.scope, [k]: v } }));
  const updAction = (k, v) => setForm((f) => ({ ...f, action: { ...f.action, [k]: v } }));

  const submit = async () => {
    if (!form.name?.trim()) { toast.error('Rule name required'); return; }
    setBusy(true);
    try {
      const cleanScope = Object.fromEntries(
        Object.entries(form.scope).filter(([, v]) => v !== '' && v != null)
      );
      const action = { ...form.action };
      action.margin_pct = action.margin_pct === '' ? null : Number(action.margin_pct);
      action.amount = action.amount === '' ? null : Number(action.amount);
      action.price = action.price === '' ? null : Number(action.price);
      const body = {
        name: form.name,
        scope: cleanScope,
        action,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
        manual_priority: parseInt(form.manual_priority) || 0,
        active: !!form.active,
        notes: form.notes || null,
      };
      if (initial?.id) await updatePriceRule(initial.id, body);
      else await createPriceRule(body);
      toast.success(initial ? 'Rule updated' : 'Rule created');
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(errMsg(e, 'Save failed')); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="pricing-rule-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit price rule' : 'New price rule'}</DialogTitle>
          <DialogDescription>Higher specificity wins (customer + SKU &gt; customer &gt; SKU &gt; category &gt; service).</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Rule name</Label><Input data-testid="rule-name" value={form.name} onChange={(e) => upd('name', e.target.value)} /></div>
        </div>

        <div className="mt-3 border-t pt-3">
          <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground mb-2">Scope (leave blank to match all)</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Customer email</Label><Input data-testid="rule-scope-customer" value={form.scope.customer_email || ''} onChange={(e) => updScope('customer_email', e.target.value)} /></div>
            <div><Label>SKU</Label><Input data-testid="rule-scope-sku" value={form.scope.sku || ''} onChange={(e) => updScope('sku', e.target.value)} /></div>
            <div><Label>Category</Label><Input data-testid="rule-scope-category" value={form.scope.category || ''} onChange={(e) => updScope('category', e.target.value)} /></div>
            <div><Label>Service type</Label><Input data-testid="rule-scope-service" value={form.scope.service_type || ''} onChange={(e) => updScope('service_type', e.target.value)} placeholder="e.g. storage_pallet_day" /></div>
          </div>
        </div>

        <div className="mt-3 border-t pt-3">
          <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground mb-2">Action</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Kind</Label>
              <Select value={form.action.kind} onValueChange={(v) => updAction('kind', v)}>
                <SelectTrigger data-testid="rule-action-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{RULE_KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Currency</Label><Input value={form.action.currency || 'AUD'} onChange={(e) => updAction('currency', e.target.value.toUpperCase())} /></div>
            {form.action.kind === 'percentage' && (
              <div><Label>Margin %</Label><Input data-testid="rule-action-margin" type="number" value={form.action.margin_pct ?? 0} onChange={(e) => updAction('margin_pct', e.target.value)} /></div>
            )}
            {form.action.kind === 'fixed_markup' && (
              <div><Label>Amount ($)</Label><Input data-testid="rule-action-amount" type="number" value={form.action.amount ?? 0} onChange={(e) => updAction('amount', e.target.value)} /></div>
            )}
            {form.action.kind === 'fixed_price' && (
              <div><Label>Price ($)</Label><Input data-testid="rule-action-price" type="number" value={form.action.price ?? 0} onChange={(e) => updAction('price', e.target.value)} /></div>
            )}
            {form.action.kind === 'tiered' && (
              <div className="col-span-2">
                <Label>Brackets (min ≤ cost &lt; max → margin %)</Label>
                <div className="space-y-2 mt-1">
                  {(form.action.brackets || []).map((b, i) => (
                    <div key={i} className="flex gap-2 items-end" data-testid={`rule-bracket-${i}`}>
                      <Input className="w-24" type="number" placeholder="min" value={b.min ?? 0} onChange={(e) => {
                        const list = [...form.action.brackets]; list[i] = { ...b, min: Number(e.target.value) }; updAction('brackets', list);
                      }} />
                      <Input className="w-24" type="number" placeholder="max (∞=blank)" value={b.max ?? ''} onChange={(e) => {
                        const list = [...form.action.brackets]; list[i] = { ...b, max: e.target.value === '' ? null : Number(e.target.value) }; updAction('brackets', list);
                      }} />
                      <Input className="w-24" type="number" placeholder="margin %" value={b.margin_pct ?? 0} onChange={(e) => {
                        const list = [...form.action.brackets]; list[i] = { ...b, margin_pct: Number(e.target.value) }; updAction('brackets', list);
                      }} />
                      <Button size="sm" variant="outline" onClick={() => updAction('brackets', form.action.brackets.filter((_, j) => j !== i))}>Remove</Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" data-testid="rule-add-bracket" onClick={() => updAction('brackets', [...(form.action.brackets || []), { min: 0, max: null, margin_pct: 0 }])}>+ Add bracket</Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 border-t pt-3 grid grid-cols-2 gap-3">
          <div><Label>Starts at (UTC, optional)</Label>
            <Input type="datetime-local" data-testid="rule-starts-at"
              value={form.starts_at ? new Date(form.starts_at).toISOString().slice(0, 16) : ''}
              onChange={(e) => upd('starts_at', e.target.value ? new Date(e.target.value).toISOString() : null)} />
          </div>
          <div><Label>Ends at (UTC, optional)</Label>
            <Input type="datetime-local" data-testid="rule-ends-at"
              value={form.ends_at ? new Date(form.ends_at).toISOString().slice(0, 16) : ''}
              onChange={(e) => upd('ends_at', e.target.value ? new Date(e.target.value).toISOString() : null)} />
          </div>
          <div><Label>Manual priority boost</Label><Input type="number" value={form.manual_priority} onChange={(e) => upd('manual_priority', e.target.value)} /></div>
          <div className="flex items-end gap-2"><Label className="mb-2">Active</Label>
            <input type="checkbox" data-testid="rule-active" className="h-4 w-4" checked={!!form.active} onChange={(e) => upd('active', e.target.checked)} />
          </div>
          <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes || ''} onChange={(e) => upd('notes', e.target.value)} rows={2} /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="rule-save" onClick={submit} disabled={busy}>{busy ? 'Saving…' : initial ? 'Save' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Rate Card Editor
// =============================================================================
function RateCardEditor({ open, onClose, initial, onSaved }) {
  const blank = {
    name: '', customer_email: '', notes: '',
    storage: [], receiving: [], pickpack: [], freight: [], pallet: [], valueadd: [],
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      const init = initial ? { ...blank, ...initial } : blank;
      RATE_CATEGORIES.forEach((c) => { init[c] = init[c] || []; });
      setForm(init);
    }
  }, [open, initial]);

  const updRow = (cat, i, k, v) => setForm((f) => {
    const list = [...f[cat]];
    list[i] = { ...list[i], [k]: k === 'rate' ? Number(v) : v };
    return { ...f, [cat]: list };
  });
  const addRow = (cat) => setForm((f) => ({ ...f, [cat]: [...f[cat], { service_code: '', label: '', unit: 'ea', rate: 0, currency: 'AUD' }] }));
  const removeRow = (cat, i) => setForm((f) => ({ ...f, [cat]: f[cat].filter((_, j) => j !== i) }));

  const submit = async () => {
    if (!form.name?.trim()) { toast.error('Rate card name required'); return; }
    setBusy(true);
    try {
      const body = { ...form };
      if (initial?.id) await updateRateCard(initial.id, body);
      else await createRateCard(body);
      toast.success(initial ? 'Rate card updated' : 'Rate card created');
      onSaved?.(); onClose?.();
    } catch (e) { toast.error(errMsg(e, 'Save failed')); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ratecard-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit rate card' : 'New rate card'}</DialogTitle>
          <DialogDescription>Customer-specific or tenant-default contract rates by service category.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div><Label>Card name</Label><Input data-testid="ratecard-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><Label>Customer email (blank = tenant default)</Label><Input data-testid="ratecard-customer" value={form.customer_email || ''} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>

        {RATE_CATEGORIES.map((cat) => (
          <div key={cat} className="border-t pt-3 mt-3" data-testid={`ratecard-section-${cat}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground">{cat}</div>
              <Button size="sm" variant="outline" onClick={() => addRow(cat)} data-testid={`ratecard-add-${cat}`}>+ Add row</Button>
            </div>
            {(form[cat] || []).length === 0 ? (
              <div className="text-xs text-muted-foreground italic">no rows</div>
            ) : (
              <div className="space-y-1">
                <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                  <div className="col-span-3">Service code</div>
                  <div className="col-span-4">Label</div>
                  <div className="col-span-2">Unit</div>
                  <div className="col-span-1">Rate</div>
                  <div className="col-span-1">CCY</div>
                  <div className="col-span-1"></div>
                </div>
                {form[cat].map((r, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <Input className="col-span-3" value={r.service_code || ''} onChange={(e) => updRow(cat, i, 'service_code', e.target.value)} />
                    <Input className="col-span-4" value={r.label || ''} onChange={(e) => updRow(cat, i, 'label', e.target.value)} />
                    <Input className="col-span-2" value={r.unit || 'ea'} onChange={(e) => updRow(cat, i, 'unit', e.target.value)} />
                    <Input className="col-span-1" type="number" value={r.rate ?? 0} onChange={(e) => updRow(cat, i, 'rate', e.target.value)} />
                    <Input className="col-span-1" value={r.currency || 'AUD'} onChange={(e) => updRow(cat, i, 'currency', e.target.value.toUpperCase())} />
                    <Button className="col-span-1" size="sm" variant="ghost" onClick={() => removeRow(cat, i)}>×</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="ratecard-save" onClick={submit} disabled={busy}>{busy ? 'Saving…' : initial ? 'Save' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Customer SKU Editor
// =============================================================================
function CustomerSkuEditor({ open, onClose, initial, onSaved }) {
  const blank = { customer_email: '', sku: '', price: 0, currency: 'AUD', moq: 0, pack_size: '', active: true, notes: '' };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(initial ? { ...blank, ...initial } : blank); }, [open, initial]);

  const submit = async () => {
    if (!form.customer_email?.trim() || !form.sku?.trim()) { toast.error('Email + SKU required'); return; }
    setBusy(true);
    try {
      const body = {
        ...form,
        price: Number(form.price) || 0,
        moq: parseInt(form.moq) || 0,
        pack_size: form.pack_size === '' ? null : parseInt(form.pack_size) || null,
      };
      await upsertCustomerSku(body);
      toast.success('Customer SKU saved');
      onSaved?.(); onClose?.();
    } catch (e) { toast.error(errMsg(e, 'Save failed')); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="csku-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit customer SKU' : 'New customer SKU override'}</DialogTitle>
          <DialogDescription>Highest-precedence pricing for a single customer × SKU combo.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Customer email</Label><Input data-testid="csku-email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} disabled={!!initial} /></div>
          <div><Label>SKU</Label><Input data-testid="csku-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} disabled={!!initial} /></div>
          <div><Label>Price</Label><Input data-testid="csku-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
          <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} /></div>
          <div><Label>MOQ</Label><Input type="number" value={form.moq} onChange={(e) => setForm({ ...form, moq: e.target.value })} /></div>
          <div><Label>Pack size</Label><Input type="number" value={form.pack_size ?? ''} onChange={(e) => setForm({ ...form, pack_size: e.target.value })} /></div>
          <div className="flex items-end gap-2"><Label className="mb-2">Active</Label>
            <input type="checkbox" className="h-4 w-4" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          </div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="csku-save" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Resolve Tester widget
// =============================================================================
function ResolveTester() {
  const [ctx, setCtx] = useState({ customer_email: '', sku: '', category: '', service_type: '', base_cost: 0, qty: 1 });
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setCtx((c) => ({ ...c, [k]: v }));
  const run = async () => {
    setBusy(true); setOut(null);
    try {
      const body = { ...ctx, base_cost: Number(ctx.base_cost) || 0, qty: parseInt(ctx.qty) || 1 };
      const r = await resolvePrice(body);
      setOut(r.data);
    } catch (e) { toast.error(errMsg(e, 'Resolve failed')); } finally { setBusy(false); }
  };
  return (
    <div className="border border-border p-4 rounded" data-testid="resolve-tester">
      <div className="flex items-center gap-2 mb-3">
        <Calculator size={18} weight="duotone" className="text-brand" />
        <div className="font-semibold tracking-tight">Resolve price tester</div>
        <div className="ml-auto text-xs text-muted-foreground">Inspect which rule wins for a given context.</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div><Label className="text-xs">Customer email</Label><Input data-testid="resolve-email" value={ctx.customer_email} onChange={(e) => upd('customer_email', e.target.value)} /></div>
        <div><Label className="text-xs">SKU</Label><Input data-testid="resolve-sku" value={ctx.sku} onChange={(e) => upd('sku', e.target.value)} /></div>
        <div><Label className="text-xs">Category</Label><Input value={ctx.category} onChange={(e) => upd('category', e.target.value)} /></div>
        <div><Label className="text-xs">Service type</Label><Input value={ctx.service_type} onChange={(e) => upd('service_type', e.target.value)} /></div>
        <div><Label className="text-xs">Base cost</Label><Input data-testid="resolve-base-cost" type="number" value={ctx.base_cost} onChange={(e) => upd('base_cost', e.target.value)} /></div>
        <div><Label className="text-xs">Quantity</Label><Input type="number" value={ctx.qty} onChange={(e) => upd('qty', e.target.value)} /></div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" data-testid="resolve-run" onClick={run} disabled={busy}>{busy ? 'Resolving…' : 'Resolve price'}</Button>
        {out && (
          <div className="text-sm" data-testid="resolve-result">
            <span className="font-mono font-bold">{out.currency} {Number(out.sell_price).toFixed(4)}</span>
            <span className="ml-3 text-muted-foreground">margin {out.margin_pct}%</span>
            <Badge variant="outline" className="ml-3" data-testid="resolve-source">{out.applied?.source}</Badge>
          </div>
        )}
      </div>
      {out?.explain?.length > 0 && (
        <ul className="mt-2 text-xs text-muted-foreground list-disc pl-5">
          {out.explain.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      )}
    </div>
  );
}

// =============================================================================
// Quote Webhook settings card (Phase 2.1)
// =============================================================================
function QuoteWebhookSettings() {
  const [s, setS] = useState(null);
  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [log, setLog] = useState([]);

  const load = async () => {
    try {
      const r = await getPricingSettings();
      setS(r.data); setUrl(r.data?.quote_webhook_url || ''); setEnabled(!!r.data?.quote_webhook_enabled);
    } catch (e) { /* settings endpoint blocked for non-admin — silent */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const body = { quote_webhook_url: url, quote_webhook_enabled: enabled };
      if (secret.trim()) body.quote_webhook_secret = secret.trim();
      const r = await updatePricingSettings(body);
      setS(r.data); setSecret('');
      toast.success('Webhook settings saved');
    } catch (e) { toast.error(errMsg(e, 'Save failed')); } finally { setBusy(false); }
  };

  const openLog = async () => {
    try { const r = await listQuoteWebhookLog(); setLog(r.data?.items || []); setLogOpen(true); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!s) return null;
  return (
    <div className="border border-border p-4 mb-4" data-testid="webhook-settings">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold tracking-tight flex items-center gap-2">
          <PaperPlaneTilt size={16} weight="duotone" className="text-brand" /> Quote acceptance webhook
        </div>
        <Button size="sm" variant="outline" onClick={openLog} data-testid="webhook-log-open">View log</Button>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Fires <code className="text-[10px] bg-muted px-1">POST</code> to your URL when a customer accepts/declines a public quote.
        Signed with HMAC-SHA256 in <code className="text-[10px] bg-muted px-1">X-Stockaris-Signature</code> when a secret is set.
      </p>
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-6"><Label className="text-xs">Webhook URL</Label>
          <Input data-testid="webhook-url" placeholder="https://your-server.example.com/quote-events" value={url} onChange={(e) => setUrl(e.target.value)} />
        </div>
        <div className="col-span-3"><Label className="text-xs">Secret {s.has_secret && <span className="text-emerald-600">· set</span>}</Label>
          <Input data-testid="webhook-secret" type="password" placeholder={s.has_secret ? '·······' : 'optional HMAC secret'} value={secret} onChange={(e) => setSecret(e.target.value)} />
        </div>
        <div className="col-span-1 flex items-center gap-1"><input type="checkbox" data-testid="webhook-enabled" className="h-4 w-4" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /><Label className="text-xs">on</Label></div>
        <div className="col-span-2"><Button size="sm" data-testid="webhook-save" onClick={save} disabled={busy} className="w-full">{busy ? 'Saving…' : 'Save'}</Button></div>
      </div>
      {s.last_webhook_at && (
        <div className="mt-2 text-xs text-muted-foreground" data-testid="webhook-last">
          Last fired {new Date(s.last_webhook_at).toLocaleString()} · status: <span className={s.last_webhook_status?.startsWith('ok') ? 'text-emerald-600' : 'text-rose-600'}>{s.last_webhook_status}</span>
        </div>
      )}

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="webhook-log-dialog">
          <DialogHeader>
            <DialogTitle>Quote webhook log</DialogTitle>
            <DialogDescription>Latest 50 attempts, newest first.</DialogDescription>
          </DialogHeader>
          {log.length === 0 ? (
            <div className="text-sm text-muted-foreground p-6 text-center">No webhooks fired yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-widest">
                <tr><th className="text-left p-2">When</th><th className="text-left p-2">Event</th><th className="text-left p-2">Quote</th><th className="text-right p-2">Status</th></tr>
              </thead>
              <tbody>
                {log.map((r) => (
                  <tr key={r.id} className="border-t border-border" data-testid={`webhook-log-row-${r.id}`}>
                    <td className="p-2 font-mono text-xs">{(r.fired_at || '').slice(0, 19).replace('T', ' ')}</td>
                    <td className="p-2"><Badge variant="outline">{r.event}</Badge></td>
                    <td className="p-2 font-mono text-xs">{(r.quote_id || '').slice(-8)}</td>
                    <td className="p-2 text-right">
                      {r.ok ? <Badge className="bg-emerald-600">{r.status_code} OK</Badge>
                            : <Badge className="bg-rose-600">{r.status_code || 'err'}</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =============================================================================
// Profitability Panel (Phase 3)
// =============================================================================
function ProfitabilityPanel() {
  const [windowDays, setWindowDays] = useState(30);
  const [groupBy, setGroupBy] = useState('customer');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const r = await getProfitability(windowDays, groupBy); setData(r.data); }
    catch (e) { toast.error(errMsg(e, 'Failed to load profitability')); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [windowDays, groupBy]);

  const totals = data?.totals || {};
  const rows = data?.rows || [];
  return (
    <div data-testid="profitability-panel">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <Label className="text-xs">Window (days)</Label>
          <div className="flex gap-1">
            {[7, 30, 90, 180, 365].map((d) => (
              <Button key={d} size="sm" variant={windowDays === d ? 'default' : 'outline'} data-testid={`profit-window-${d}`} onClick={() => setWindowDays(d)}>{d}d</Button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs">Group by</Label>
          <div className="flex gap-1">
            <Button size="sm" variant={groupBy === 'customer' ? 'default' : 'outline'} data-testid="profit-group-customer" onClick={() => setGroupBy('customer')}>Customer</Button>
            <Button size="sm" variant={groupBy === 'service' ? 'default' : 'outline'} data-testid="profit-group-service" onClick={() => setGroupBy('service')}>Service</Button>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={busy}>{busy ? 'Loading…' : 'Refresh'}</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="profit-kpis">
        <div className="border border-border p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Revenue</div>
          <div className="text-2xl font-bold tabular-nums" data-testid="kpi-revenue">${(totals.revenue ?? 0).toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">{totals.rows ?? 0} charges</div>
        </div>
        <div className="border border-border p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Margin</div>
          <div className="text-2xl font-bold tabular-nums" data-testid="kpi-margin">${(totals.margin_amount ?? 0).toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">{totals.margin_pct == null ? '—' : `${totals.margin_pct}%`} (known cost rows)</div>
        </div>
        <div className="border border-border p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cost (known)</div>
          <div className="text-2xl font-bold tabular-nums">${(totals.cost_known ?? 0).toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">via applied_pricing.margin_pct</div>
        </div>
        <div className="border border-border p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cost-unknown $</div>
          <div className="text-2xl font-bold tabular-nums text-amber-600">${(totals.cost_unknown_amount ?? 0).toLocaleString()}</div>
          <div className="text-xs text-muted-foreground">No margin recorded — out of margin calc</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground p-6 border border-dashed border-border text-center">No charges in this window.</div>
      ) : (
        <div className="border border-border" data-testid="profit-rows">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-widest">
              <tr><th className="text-left p-2">{groupBy === 'customer' ? 'Customer' : 'Service code'}</th><th className="text-right p-2">Revenue</th><th className="text-right p-2">Cost (known)</th><th className="text-right p-2">Margin $</th><th className="text-right p-2">Margin %</th><th className="text-right p-2">Charges</th><th className="text-right p-2">Cost-unknown $</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} data-testid={`profit-row-${r.key}`} className="border-t border-border">
                  <td className="p-2 font-medium">{r.label}</td>
                  <td className="p-2 text-right tabular-nums">${r.revenue.toLocaleString()}</td>
                  <td className="p-2 text-right tabular-nums">${r.cost_known.toLocaleString()}</td>
                  <td className="p-2 text-right tabular-nums">${r.margin_amount.toLocaleString()}</td>
                  <td className="p-2 text-right tabular-nums">
                    {r.margin_pct == null ? <span className="text-muted-foreground">—</span> :
                      <Badge className={r.margin_pct >= 20 ? 'bg-emerald-600' : r.margin_pct >= 10 ? 'bg-amber-500' : 'bg-rose-600'}>{r.margin_pct}%</Badge>}
                  </td>
                  <td className="p-2 text-right tabular-nums">{r.rows}</td>
                  <td className="p-2 text-right tabular-nums text-muted-foreground">${r.cost_unknown_amount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Quote Editor (Phase 2)
// =============================================================================
function QuoteEditor({ open, onClose, initial, onSaved }) {
  const blank = {
    customer_email: '', customer_name: '', title: 'Service Quote', currency: 'AUD',
    lines: [],
    valid_until: null, notes: '',
    approval_threshold_amount: 5000, approval_threshold_margin_pct: 10,
  };
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(initial ? { ...blank, ...initial } : blank); }, [open, initial]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updLine = (i, k, v) => setForm((f) => {
    const list = [...f.lines]; list[i] = { ...list[i], [k]: ['qty', 'unit_price', 'base_cost'].includes(k) ? (v === '' ? null : Number(v)) : v };
    return { ...f, lines: list };
  });
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { service_code: '', label: '', unit: 'ea', qty: 1, unit_price: 0, base_cost: null }] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }));

  const totals = useMemo(() => {
    const revenue = form.lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
    let cost = 0; let costKnown = true;
    for (const l of form.lines) {
      if (l.base_cost == null || l.base_cost === '') { costKnown = false; continue; }
      cost += Number(l.base_cost) * (Number(l.qty) || 0);
    }
    return {
      revenue: Math.round(revenue * 100) / 100,
      cost: costKnown ? Math.round(cost * 100) / 100 : null,
      margin_amount: costKnown ? Math.round((revenue - cost) * 100) / 100 : null,
      margin_pct: costKnown && revenue > 0 ? Math.round((revenue - cost) / revenue * 10000) / 100 : null,
    };
  }, [form.lines]);

  const submit = async () => {
    if (!form.customer_email?.trim()) { toast.error('Customer email required'); return; }
    if (form.lines.length === 0) { toast.error('Add at least one quote line'); return; }
    setBusy(true);
    try {
      const body = {
        ...form,
        approval_threshold_amount: Number(form.approval_threshold_amount) || 0,
        approval_threshold_margin_pct: Number(form.approval_threshold_margin_pct) || 0,
        valid_until: form.valid_until || null,
      };
      if (initial?.id) await updateQuote(initial.id, body);
      else await createQuote(body);
      toast.success(initial ? 'Quote updated' : 'Quote created');
      onSaved?.(); onClose?.();
    } catch (e) { toast.error(errMsg(e, 'Save failed')); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle>{initial ? 'Edit quote' : 'New quote'}</DialogTitle>
          <DialogDescription>Build a customer-facing service quote with embedded approval thresholds.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Customer email</Label><Input data-testid="quote-email" value={form.customer_email} onChange={(e) => upd('customer_email', e.target.value)} /></div>
          <div><Label>Customer name (optional)</Label><Input data-testid="quote-name" value={form.customer_name || ''} onChange={(e) => upd('customer_name', e.target.value)} /></div>
          <div className="col-span-2"><Label>Title</Label><Input data-testid="quote-title" value={form.title} onChange={(e) => upd('title', e.target.value)} /></div>
          <div><Label>Currency</Label><Input value={form.currency} onChange={(e) => upd('currency', e.target.value.toUpperCase())} /></div>
          <div><Label>Valid until (UTC)</Label>
            <Input type="datetime-local" value={form.valid_until ? new Date(form.valid_until).toISOString().slice(0, 16) : ''}
              onChange={(e) => upd('valid_until', e.target.value ? new Date(e.target.value).toISOString() : null)} />
          </div>
        </div>

        <div className="border-t pt-3 mt-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Line items</div>
            <Button size="sm" variant="outline" data-testid="quote-add-line" onClick={addLine}>+ Add line</Button>
          </div>
          {form.lines.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">no lines yet</div>
          ) : (
            <div className="space-y-1">
              <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                <div className="col-span-2">Service</div>
                <div className="col-span-3">Label</div>
                <div className="col-span-1">Unit</div>
                <div className="col-span-1">Qty</div>
                <div className="col-span-2">Unit price</div>
                <div className="col-span-2">Base cost (opt.)</div>
                <div className="col-span-1"></div>
              </div>
              {form.lines.map((l, i) => (
                <div key={i} className="grid grid-cols-12 gap-2" data-testid={`quote-line-${i}`}>
                  <Input className="col-span-2" value={l.service_code || ''} onChange={(e) => updLine(i, 'service_code', e.target.value)} />
                  <Input className="col-span-3" value={l.label || ''} onChange={(e) => updLine(i, 'label', e.target.value)} />
                  <Input className="col-span-1" value={l.unit || 'ea'} onChange={(e) => updLine(i, 'unit', e.target.value)} />
                  <Input className="col-span-1" type="number" value={l.qty ?? 1} onChange={(e) => updLine(i, 'qty', e.target.value)} />
                  <Input className="col-span-2" type="number" value={l.unit_price ?? 0} onChange={(e) => updLine(i, 'unit_price', e.target.value)} />
                  <Input className="col-span-2" type="number" value={l.base_cost ?? ''} placeholder="cost" onChange={(e) => updLine(i, 'base_cost', e.target.value)} />
                  <Button className="col-span-1" size="sm" variant="ghost" onClick={() => removeLine(i)}>×</Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t pt-3 mt-3 grid grid-cols-4 gap-3 text-sm" data-testid="quote-totals">
          <div><div className="text-[10px] uppercase text-muted-foreground">Revenue</div><div className="font-bold tabular-nums">{form.currency} {totals.revenue.toFixed(2)}</div></div>
          <div><div className="text-[10px] uppercase text-muted-foreground">Cost</div><div className="font-bold tabular-nums">{totals.cost == null ? '—' : `${form.currency} ${totals.cost.toFixed(2)}`}</div></div>
          <div><div className="text-[10px] uppercase text-muted-foreground">Margin $</div><div className="font-bold tabular-nums">{totals.margin_amount == null ? '—' : `${form.currency} ${totals.margin_amount.toFixed(2)}`}</div></div>
          <div><div className="text-[10px] uppercase text-muted-foreground">Margin %</div><div className="font-bold tabular-nums">{totals.margin_pct == null ? '—' : `${totals.margin_pct}%`}</div></div>
        </div>

        <div className="border-t pt-3 mt-3 grid grid-cols-2 gap-3">
          <div><Label>Auto-approve under ($)</Label><Input data-testid="quote-thresh-amount" type="number" value={form.approval_threshold_amount} onChange={(e) => upd('approval_threshold_amount', e.target.value)} /></div>
          <div><Label>Auto-approve at margin% ≥</Label><Input data-testid="quote-thresh-margin" type="number" value={form.approval_threshold_margin_pct} onChange={(e) => upd('approval_threshold_margin_pct', e.target.value)} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={(e) => upd('notes', e.target.value)} /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="quote-save" onClick={submit} disabled={busy}>{busy ? 'Saving…' : initial ? 'Save' : 'Create draft'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Quotes Panel (Phase 2)
// =============================================================================
function QuotesPanel() {
  const [quotes, setQuotes] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [busy, setBusy] = useState({});

  const load = async () => {
    try { const r = await listQuotes(statusFilter ? { status: statusFilter } : {}); setQuotes(r.data?.items || []); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const act = async (id, fn, label) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try { await fn(id); toast.success(label); await load(); }
    catch (e) { toast.error(errMsg(e, 'Action failed')); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const replay = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await replayQuoteWebhook(id);
      const d = r.data || {};
      if (d.skipped) toast.warning(`Webhook skipped — ${d.reason === 'no_url' ? 'no URL configured' : 'disabled in settings'}`);
      else if (d.ok) toast.success(`Replayed ${d.event} — HTTP ${d.status_code}`);
      else toast.error(`Replay failed — ${d.status_code ? 'HTTP ' + d.status_code : 'no response'}`);
    } catch (e) { toast.error(errMsg(e, 'Replay failed')); }
    finally { setBusy((b) => ({ ...b, [id]: false })); }
  };

  const doDelete = async () => {
    try { await deleteQuote(pendingDelete.id); toast.success('Quote deleted'); setPendingDelete(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const copyPublicLink = (q) => {
    if (!q.public_token) { toast.error('Send the quote first to mint a public link'); return; }
    const url = `${window.location.origin}/quote/${q.public_token}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Public link copied'));
  };

  return (
    <div data-testid="quotes-panel">
      <QuoteWebhookSettings />
      <div className="flex items-center gap-2 mb-3">
        <Label className="text-xs">Status</Label>
        <select data-testid="quotes-status-filter" className="border border-border px-2 py-1 text-sm bg-background" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All</option>
          {Object.keys(QUOTE_STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="ml-auto" />
        <Button size="sm" data-testid="quotes-new" onClick={() => { setEditing(null); setOpen(true); }}>
          <Plus size={14} className="mr-1" /> New quote
        </Button>
      </div>
      {quotes.length === 0 ? (
        <div className="text-sm text-muted-foreground p-6 border border-dashed border-border text-center">No quotes match.</div>
      ) : (
        <div className="border border-border" data-testid="quotes-list">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-widest">
              <tr><th className="text-left p-2">Title</th><th className="text-left p-2">Customer</th><th className="text-right p-2">Revenue</th><th className="text-right p-2">Margin</th><th className="text-left p-2">Status</th><th className="text-right p-2">Actions</th></tr>
            </thead>
            <tbody>
              {quotes.map((q) => {
                const t = q.totals || {};
                const isBusy = !!busy[q.id];
                return (
                  <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-t border-border">
                    <td className="p-2 font-medium">{q.title}</td>
                    <td className="p-2 text-xs"><div className="font-mono">{q.customer_email}</div>{q.customer_name && <div className="text-muted-foreground">{q.customer_name}</div>}</td>
                    <td className="p-2 text-right tabular-nums">{q.currency} {(t.revenue ?? 0).toLocaleString()}</td>
                    <td className="p-2 text-right tabular-nums">{t.margin_pct == null ? '—' : `${t.margin_pct}%`}</td>
                    <td className="p-2">
                      <Badge className={QUOTE_STATUS_COLORS[q.status] || 'bg-zinc-200'} data-testid={`quote-status-${q.id}`}>{q.status}</Badge>
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {(q.status === 'draft' || q.status === 'rejected') && (
                        <>
                          <Button size="sm" variant="ghost" data-testid={`quote-edit-${q.id}`} onClick={() => { setEditing(q); setOpen(true); }} disabled={isBusy}><Pencil size={14} /></Button>
                          <Button size="sm" variant="outline" className="ml-1" data-testid={`quote-submit-${q.id}`} onClick={() => act(q.id, submitQuote, 'Submitted')} disabled={isBusy}>Submit</Button>
                          <Button size="sm" variant="ghost" data-testid={`quote-delete-${q.id}`} onClick={() => setPendingDelete(q)} disabled={isBusy}><Trash size={14} className="text-rose-600" /></Button>
                        </>
                      )}
                      {q.status === 'pending_approval' && (
                        <>
                          <Button size="sm" variant="default" data-testid={`quote-approve-${q.id}`} onClick={() => act(q.id, approveQuote, 'Approved')} disabled={isBusy}><Check size={14} className="mr-1" />Approve</Button>
                          <Button size="sm" variant="outline" className="ml-1" data-testid={`quote-reject-${q.id}`} onClick={() => act(q.id, rejectQuote, 'Rejected')} disabled={isBusy}><XIcon size={14} className="mr-1" />Reject</Button>
                        </>
                      )}
                      {q.status === 'approved' && (
                        <Button size="sm" variant="default" data-testid={`quote-send-${q.id}`} onClick={() => act(q.id, sendQuote, 'Sent to customer')} disabled={isBusy}><PaperPlaneTilt size={14} className="mr-1" />Send</Button>
                      )}
                      {q.status === 'sent_to_customer' && (
                        <Button size="sm" variant="outline" data-testid={`quote-link-${q.id}`} onClick={() => copyPublicLink(q)}>Copy link</Button>
                      )}
                      {(q.status === 'accepted' || q.status === 'declined') && (
                        <Button size="sm" variant="outline" data-testid={`quote-replay-${q.id}`} onClick={() => replay(q.id)} disabled={isBusy} title="Re-fire the acceptance webhook">
                          <PaperPlaneTilt size={14} className="mr-1" />Replay webhook
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <QuoteEditor open={open} initial={editing} onClose={() => setOpen(false)} onSaved={load} />
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="quote-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete quote?</AlertDialogTitle><AlertDialogDescription>"{pendingDelete?.title}" will be removed.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDelete} data-testid="quote-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// =============================================================================
// Main page
// =============================================================================
export default function PricingRules() {
  const [tab, setTab] = useState('profiles');

  // Profiles
  const [profiles, setProfiles] = useState([]);
  const [profileEdit, setProfileEdit] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileDel, setProfileDel] = useState(null);

  // Rules
  const [rules, setRules] = useState([]);
  const [ruleEdit, setRuleEdit] = useState(null);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [ruleDel, setRuleDel] = useState(null);

  // Rate cards
  const [rateCards, setRateCards] = useState([]);
  const [rcEdit, setRcEdit] = useState(null);
  const [rcOpen, setRcOpen] = useState(false);
  const [rcDel, setRcDel] = useState(null);

  // Customer SKUs
  const [cskus, setCskus] = useState([]);
  const [cskuQuery, setCskuQuery] = useState({ customer_email: '', sku: '' });
  const [cskuEdit, setCskuEdit] = useState(null);
  const [cskuOpen, setCskuOpen] = useState(false);
  const [cskuDel, setCskuDel] = useState(null);

  // Audit
  const [audit, setAudit] = useState([]);
  const [auditError, setAuditError] = useState(null);

  const loadProfiles = async () => { try { const r = await listPricingProfiles(); setProfiles(r.data?.items || []); } catch (e) { toast.error(errMsg(e)); } };
  const loadRules = async () => { try { const r = await listPriceRules(); setRules(r.data?.items || []); } catch (e) { toast.error(errMsg(e)); } };
  const loadRateCards = async () => { try { const r = await listRateCards(); setRateCards(r.data?.items || []); } catch (e) { toast.error(errMsg(e)); } };
  const loadCskus = async () => { try { const r = await listCustomerSkus(cskuQuery); setCskus(r.data?.items || []); } catch (e) { toast.error(errMsg(e)); } };
  const loadAudit = async () => {
    try { const r = await listPriceAudit({ limit: 200 }); setAudit(r.data?.items || []); setAuditError(null); }
    catch (e) { const msg = errMsg(e, 'Failed to load audit'); setAuditError(msg); toast.error(msg); }
  };

  useEffect(() => { loadProfiles(); loadRules(); loadRateCards(); }, []);
  useEffect(() => { if (tab === 'cskus') loadCskus(); if (tab === 'audit') loadAudit(); /* eslint-disable-next-line */ }, [tab]);

  const counts = useMemo(() => ({
    profiles: profiles.length, rules: rules.length, rate_cards: rateCards.length,
  }), [profiles, rules, rateCards]);

  const doDeleteProfile = async () => {
    try { await deletePricingProfile(profileDel.id); toast.success('Profile deleted'); setProfileDel(null); loadProfiles(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const doDeleteRule = async () => {
    try { await deletePriceRule(ruleDel.id); toast.success('Rule deleted'); setRuleDel(null); loadRules(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const doDeleteRateCard = async () => {
    try { await deleteRateCard(rcDel.id); toast.success('Rate card deleted'); setRcDel(null); loadRateCards(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const doDeleteCsku = async () => {
    try { await deleteCustomerSku(cskuDel.id); toast.success('Customer SKU deleted'); setCskuDel(null); loadCskus(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="pricing-rules-page">
        <div className="flex items-center gap-3 mb-1">
          <Coins size={28} weight="duotone" className="text-brand" />
          <h1 className="text-2xl font-bold tracking-tight">Commercial Pricing</h1>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Customer profiles · multi-level rules · rate cards · per-SKU contract overrides.</p>

        <div className="mb-5"><ResolveTester /></div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="pricing-tabs">
            <TabsTrigger value="profiles" data-testid="tab-profiles">Profiles ({counts.profiles})</TabsTrigger>
            <TabsTrigger value="rules" data-testid="tab-rules">Rules ({counts.rules})</TabsTrigger>
            <TabsTrigger value="ratecards" data-testid="tab-ratecards">Rate Cards ({counts.rate_cards})</TabsTrigger>
            <TabsTrigger value="cskus" data-testid="tab-cskus">Customer SKUs</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes"><FileText size={12} className="mr-1" />Quotes</TabsTrigger>
            <TabsTrigger value="profitability" data-testid="tab-profitability"><ChartLineUp size={12} className="mr-1" />Profitability</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger>
          </TabsList>

          {/* PROFILES */}
          <TabsContent value="profiles">
            <div className="flex justify-end mb-3">
              <Button size="sm" data-testid="profiles-new" onClick={() => { setProfileEdit(null); setProfileOpen(true); }}>
                <Plus size={14} className="mr-1" /> New profile
              </Button>
            </div>
            {profiles.length === 0 ? (
              <div className="text-sm text-muted-foreground p-6 border border-dashed border-border text-center">No pricing profiles yet.</div>
            ) : (
              <div className="border border-border" data-testid="profiles-list">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-widest">
                    <tr><th className="text-left p-2">Name</th><th className="text-left p-2">Customer</th><th className="text-left p-2">Tier</th><th className="text-left p-2">Margin</th><th className="text-left p-2">Cycle</th><th className="text-left p-2">Terms</th><th className="text-right p-2">Actions</th></tr>
                  </thead>
                  <tbody>
                    {profiles.map((p) => (
                      <tr key={p.id} data-testid={`profile-row-${p.id}`} className="border-t border-border">
                        <td className="p-2 font-medium">{p.name}</td>
                        <td className="p-2 font-mono text-xs">{p.customer_email}</td>
                        <td className="p-2"><Badge variant="outline">{p.tier}</Badge></td>
                        <td className="p-2">{p.margin_type === 'tiered' ? <em>tiered</em> : `${p.default_margin}${p.margin_type === 'percentage' ? '%' : ' $'}`}</td>
                        <td className="p-2">{p.billing_cycle}</td>
                        <td className="p-2">net {p.payment_terms_days}d</td>
                        <td className="p-2 text-right">
                          <Button size="sm" variant="ghost" data-testid={`profile-edit-${p.id}`} onClick={() => { setProfileEdit(p); setProfileOpen(true); }}><Pencil size={14} /></Button>
                          <Button size="sm" variant="ghost" data-testid={`profile-delete-${p.id}`} onClick={() => setProfileDel(p)}><Trash size={14} className="text-rose-600" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* RULES */}
          <TabsContent value="rules">
            <div className="flex justify-end mb-3">
              <Button size="sm" data-testid="rules-new" onClick={() => { setRuleEdit(null); setRuleOpen(true); }}>
                <Plus size={14} className="mr-1" /> New rule
              </Button>
            </div>
            {rules.length === 0 ? (
              <div className="text-sm text-muted-foreground p-6 border border-dashed border-border text-center">No price rules yet.</div>
            ) : (
              <div className="border border-border" data-testid="rules-list">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-widest">
                    <tr><th className="text-left p-2">Priority</th><th className="text-left p-2">Name</th><th className="text-left p-2">Scope</th><th className="text-left p-2">Action</th><th className="text-left p-2">Active</th><th className="text-right p-2">Actions</th></tr>
                  </thead>
                  <tbody>
                    {rules.map((r) => {
                      const scope = r.scope || {}; const action = r.action || {};
                      const scopeBits = ['customer_email', 'sku', 'category', 'service_type'].filter((k) => scope[k]).map((k) => `${k}=${scope[k]}`).join(' · ') || 'global';
                      const actBits = action.kind === 'percentage' ? `${action.margin_pct}%` :
                                      action.kind === 'fixed_markup' ? `+$${action.amount}` :
                                      action.kind === 'fixed_price' ? `$${action.price}` :
                                      action.kind === 'tiered' ? `${(action.brackets || []).length} brackets` : '';
                      const sched = ruleScheduleStatus(r);
                      return (
                        <tr key={r.id} data-testid={`rule-row-${r.id}`} className="border-t border-border">
                          <td className="p-2 font-mono text-xs">{r.priority_score}</td>
                          <td className="p-2 font-medium">{r.name}
                            {sched === 'scheduled' && <Badge className="ml-2 bg-sky-100 text-sky-800" data-testid={`rule-sched-${r.id}`}>scheduled</Badge>}
                            {sched === 'active' && <Badge className="ml-2 bg-emerald-100 text-emerald-800" data-testid={`rule-sched-${r.id}`}>seasonal · live</Badge>}
                            {sched === 'expired' && <Badge className="ml-2 bg-zinc-200 text-zinc-700" data-testid={`rule-sched-${r.id}`}>expired</Badge>}
                          </td>
                          <td className="p-2 text-xs text-muted-foreground">{scopeBits}</td>
                          <td className="p-2"><Badge variant="outline">{action.kind}</Badge> <span className="ml-1 text-xs">{actBits}</span></td>
                          <td className="p-2">{r.active ? <Badge className="bg-emerald-600">on</Badge> : <Badge variant="outline">off</Badge>}</td>
                          <td className="p-2 text-right">
                            <Button size="sm" variant="ghost" data-testid={`rule-edit-${r.id}`} onClick={() => { setRuleEdit(r); setRuleOpen(true); }}><Pencil size={14} /></Button>
                            <Button size="sm" variant="ghost" data-testid={`rule-delete-${r.id}`} onClick={() => setRuleDel(r)}><Trash size={14} className="text-rose-600" /></Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* RATE CARDS */}
          <TabsContent value="ratecards">
            <div className="flex justify-end mb-3">
              <Button size="sm" data-testid="ratecards-new" onClick={() => { setRcEdit(null); setRcOpen(true); }}>
                <Plus size={14} className="mr-1" /> New rate card
              </Button>
            </div>
            {rateCards.length === 0 ? (
              <div className="text-sm text-muted-foreground p-6 border border-dashed border-border text-center">No rate cards yet.</div>
            ) : (
              <div className="border border-border" data-testid="ratecards-list">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-widest">
                    <tr><th className="text-left p-2">Name</th><th className="text-left p-2">Customer</th><th className="text-left p-2">Rows</th><th className="text-right p-2">Actions</th></tr>
                  </thead>
                  <tbody>
                    {rateCards.map((c) => {
                      const total = RATE_CATEGORIES.reduce((acc, k) => acc + (c[k]?.length || 0), 0);
                      return (
                        <tr key={c.id} data-testid={`ratecard-row-${c.id}`} className="border-t border-border">
                          <td className="p-2 font-medium">{c.name}</td>
                          <td className="p-2 font-mono text-xs">{c.customer_email || <em className="text-muted-foreground">tenant default</em>}</td>
                          <td className="p-2">{total} row{total === 1 ? '' : 's'} · {RATE_CATEGORIES.filter((k) => (c[k]?.length || 0) > 0).join(', ') || '—'}</td>
                          <td className="p-2 text-right">
                            <Button size="sm" variant="ghost" data-testid={`ratecard-edit-${c.id}`} onClick={() => { setRcEdit(c); setRcOpen(true); }}><Pencil size={14} /></Button>
                            <Button size="sm" variant="ghost" data-testid={`ratecard-delete-${c.id}`} onClick={() => setRcDel(c)}><Trash size={14} className="text-rose-600" /></Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* CUSTOMER SKUs */}
          <TabsContent value="cskus">
            <div className="flex flex-wrap items-end gap-2 mb-3">
              <div><Label className="text-xs">Customer email</Label><Input data-testid="cskus-q-email" className="w-60" value={cskuQuery.customer_email} onChange={(e) => setCskuQuery((q) => ({ ...q, customer_email: e.target.value }))} /></div>
              <div><Label className="text-xs">SKU</Label><Input data-testid="cskus-q-sku" className="w-40" value={cskuQuery.sku} onChange={(e) => setCskuQuery((q) => ({ ...q, sku: e.target.value }))} /></div>
              <Button size="sm" variant="outline" data-testid="cskus-search" onClick={loadCskus}>Search</Button>
              <div className="ml-auto" />
              <Button size="sm" data-testid="cskus-new" onClick={() => { setCskuEdit(null); setCskuOpen(true); }}>
                <Plus size={14} className="mr-1" /> New override
              </Button>
            </div>
            {cskus.length === 0 ? (
              <div className="text-sm text-muted-foreground p-6 border border-dashed border-border text-center">No customer-SKU overrides match.</div>
            ) : (
              <div className="border border-border" data-testid="cskus-list">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-widest">
                    <tr><th className="text-left p-2">Customer</th><th className="text-left p-2">SKU</th><th className="text-left p-2">Price</th><th className="text-left p-2">MOQ</th><th className="text-left p-2">Active</th><th className="text-right p-2">Actions</th></tr>
                  </thead>
                  <tbody>
                    {cskus.map((cs) => (
                      <tr key={cs.id} data-testid={`csku-row-${cs.id}`} className="border-t border-border">
                        <td className="p-2 font-mono text-xs">{cs.customer_email}</td>
                        <td className="p-2 font-mono">{cs.sku}</td>
                        <td className="p-2">{cs.currency} {Number(cs.price).toFixed(2)}</td>
                        <td className="p-2">{cs.moq}</td>
                        <td className="p-2">{cs.active ? <Badge className="bg-emerald-600">on</Badge> : <Badge variant="outline">off</Badge>}</td>
                        <td className="p-2 text-right">
                          <Button size="sm" variant="ghost" data-testid={`csku-edit-${cs.id}`} onClick={() => { setCskuEdit(cs); setCskuOpen(true); }}><Pencil size={14} /></Button>
                          <Button size="sm" variant="ghost" data-testid={`csku-delete-${cs.id}`} onClick={() => setCskuDel(cs)}><Trash size={14} className="text-rose-600" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* AUDIT */}
          <TabsContent value="quotes">
            <QuotesPanel />
          </TabsContent>

          <TabsContent value="profitability">
            <ProfitabilityPanel />
          </TabsContent>

          <TabsContent value="audit">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <ClockCounterClockwise size={16} /> Latest 200 changes (newest first)
              </div>
              <Button size="sm" variant="outline" onClick={loadAudit}>Refresh</Button>
            </div>
            {auditError && (
              <div className="mb-3 border border-rose-300 bg-rose-50 text-rose-800 p-3 text-sm" data-testid="audit-error">
                Failed to load audit log: {auditError}
              </div>
            )}
            {audit.length === 0 ? (
              <div className="text-sm text-muted-foreground p-6 border border-dashed border-border text-center">No audit entries yet.</div>
            ) : (
              <div className="border border-border" data-testid="audit-list">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-widest">
                    <tr><th className="text-left p-2">When</th><th className="text-left p-2">Target</th><th className="text-left p-2">Field</th><th className="text-left p-2">Old → New</th><th className="text-left p-2">Reason</th></tr>
                  </thead>
                  <tbody>
                    {audit.map((a) => (
                      <tr key={a.id} data-testid={`audit-row-${a.id}`} className="border-t border-border">
                        <td className="p-2 font-mono text-xs">{(a.created_at || '').slice(0, 19).replace('T', ' ')}</td>
                        <td className="p-2"><Tag size={12} className="inline mr-1" />{a.target_type}<span className="ml-1 text-xs text-muted-foreground">{(a.target_id || '').slice(-6)}</span></td>
                        <td className="p-2">{a.field}</td>
                        <td className="p-2 text-xs">
                          <span className="text-rose-700 line-through mr-2">{typeof a.old_value === 'object' ? '…' : String(a.old_value ?? '∅')}</span>
                          <span className="text-emerald-700">{typeof a.new_value === 'object' ? '…' : String(a.new_value ?? '∅')}</span>
                        </td>
                        <td className="p-2 text-xs text-muted-foreground">{a.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <ProfileEditor open={profileOpen} initial={profileEdit} onClose={() => setProfileOpen(false)} onSaved={loadProfiles} />
        <RuleEditor open={ruleOpen} initial={ruleEdit} onClose={() => setRuleOpen(false)} onSaved={loadRules} />
        <RateCardEditor open={rcOpen} initial={rcEdit} onClose={() => setRcOpen(false)} onSaved={loadRateCards} />
        <CustomerSkuEditor open={cskuOpen} initial={cskuEdit} onClose={() => setCskuOpen(false)} onSaved={loadCskus} />

        {/* Delete confirmations */}
        <AlertDialog open={!!profileDel} onOpenChange={(o) => !o && setProfileDel(null)}>
          <AlertDialogContent data-testid="profile-delete-dialog">
            <AlertDialogHeader><AlertDialogTitle>Delete profile?</AlertDialogTitle><AlertDialogDescription>This removes "{profileDel?.name}" — invoices already raised are unaffected.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDeleteProfile} data-testid="profile-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog open={!!ruleDel} onOpenChange={(o) => !o && setRuleDel(null)}>
          <AlertDialogContent data-testid="rule-delete-dialog">
            <AlertDialogHeader><AlertDialogTitle>Delete rule?</AlertDialogTitle><AlertDialogDescription>This removes "{ruleDel?.name}".</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDeleteRule} data-testid="rule-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog open={!!rcDel} onOpenChange={(o) => !o && setRcDel(null)}>
          <AlertDialogContent data-testid="ratecard-delete-dialog">
            <AlertDialogHeader><AlertDialogTitle>Delete rate card?</AlertDialogTitle><AlertDialogDescription>This removes "{rcDel?.name}".</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDeleteRateCard} data-testid="ratecard-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog open={!!cskuDel} onOpenChange={(o) => !o && setCskuDel(null)}>
          <AlertDialogContent data-testid="csku-delete-dialog">
            <AlertDialogHeader><AlertDialogTitle>Delete override?</AlertDialogTitle><AlertDialogDescription>{cskuDel?.customer_email} · {cskuDel?.sku}</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDeleteCsku} data-testid="csku-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
